import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { flag } from '../utils/countryFlags';
import { AlertCircle, ShoppingCart, CreditCard, Banknote, Smartphone, Building, Shield, ShieldCheck, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';

const BAGGAGE_TYPE_LABELS = {
  kilos: 'Kilos (kg)',
  enveloppes: 'Enveloppes (max 0,5kg)',
  sac_23kg: 'Sac de 23kg',
  sacs: 'Sacs',
  telephones: 'Telephones',
  ordinateurs: 'Ordinateurs'
};

const MIN_DESCRIPTION_LENGTH = 10;

const PAYMENT_METHODS = [
  // { id: 'card', label: 'Carte de crédit', icon: CreditCard, description: 'Paiement sécurisé via Stripe' }, // Masqué temporairement
  { id: 'interac', label: 'Virement Interac', icon: Building, description: 'Virement électronique Interac' },
  { id: 'orange_money', label: 'Orange Money', icon: Smartphone, description: 'Mobile Money (Cameroun)' },
  { id: 'cash', label: 'Espèces', icon: Banknote, description: 'Paiement cash au dépôt' }
];

const getBaggageUnit = (type, quantity = 1) => {
  const units = {
    kilos: 'kg', sacs: quantity > 1 ? 'sacs' : 'sac',
    enveloppes: quantity > 1 ? 'enveloppes' : 'enveloppe',
    sac_23kg: quantity > 1 ? 'sacs 23kg' : 'sac 23kg',
    telephones: quantity > 1 ? 'telephones' : 'telephone',
    ordinateurs: quantity > 1 ? 'ordinateurs' : 'ordinateur'
  };
  return units[type] || type;
};

const PurchaseModal = ({ listing, open, onOpenChange, onPurchase, processing, API, token }) => {
  const [quantity, setQuantity] = useState(1);
  const [articleDescription, setArticleDescription] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [selectedBaggage, setSelectedBaggage] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('interac');
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [declaredValue, setDeclaredValue] = useState(0);
  const [insuranceOptIn, setInsuranceOptIn] = useState(false);
  const [insuranceInfo, setInsuranceInfo] = useState(null);
  const [showLegal, setShowLegal] = useState(false);
  const [cguAccepted, setCguAccepted] = useState(false);
  const [refundAccepted, setRefundAccepted] = useState(false);
  const [logisticsAccepted, setLogisticsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Reset submitting when processing ends (parent resets after success/error)
  useEffect(() => {
    if (!processing) setSubmitting(false);
  }, [processing]);

  // useRef as backup to preserve selectedBaggage across any re-renders
  const selectedBaggageRef = useRef({});

  // Keep ref in sync
  const updateBaggage = useCallback((updater) => {
    setSelectedBaggage(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      selectedBaggageRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (open && API && token) {
      axios.get(`${API}/insurance/info`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setInsuranceInfo(res.data))
        .catch(() => {});
    }
  }, [open, API, token]);

  // Initialize baggage only when listing ID changes
  useEffect(() => {
    if (listing?.baggage_items?.length > 0) {
      const initial = {};
      listing.baggage_items.forEach(item => { initial[item.type] = 0; });
      setSelectedBaggage(initial);
      selectedBaggageRef.current = initial;
    }
  }, [listing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateInsurance = () => {
    if (!insuranceInfo || declaredValue <= 0) return { cost: 0 };
    const baseCoverage = insuranceInfo.base_coverage || 100;
    const rate = insuranceInfo.rate || 0.02;
    if (declaredValue <= baseCoverage) return { cost: 0, isFree: true };
    if (insuranceOptIn) return { cost: (declaredValue - baseCoverage) * rate };
    return { cost: 0, uncoveredAmount: declaredValue - baseCoverage };
  };

  const calculateTotal = () => {
    if (!listing?.baggage_items) return { subtotal: 0, commission: 0, insurance: 0, total: 0 };
    let subtotal = 0;
    const bags = selectedBaggageRef.current;
    listing.baggage_items.forEach(item => {
      subtotal += (bags[item.type] || 0) * item.price_per_unit;
    });
    const commission = subtotal * 0.1;
    const insurance = calculateInsurance().cost;
    return { subtotal, commission, insurance, total: subtotal + commission + insurance };
  };

  const hasSelection = () => Object.values(selectedBaggageRef.current).some(qty => qty > 0);

  // Legacy single-type calculation
  const subtotal = listing ? (quantity * listing.price_per_unit) : 0;
  const commission = subtotal * 0.1;
  const insuranceCost = calculateInsurance().cost;
  const total = subtotal + commission + insuranceCost;

  const isDescriptionValid = articleDescription.trim().length >= MIN_DESCRIPTION_LENGTH;
  const showDescriptionError = descriptionTouched && !isDescriptionValid;
  const remainingChars = MIN_DESCRIPTION_LENGTH - articleDescription.trim().length;
  const allLegalAccepted = cguAccepted && refundAccepted && logisticsAccepted;

  const handleSubmit = () => {
    if (submitting || processing) return;
    setSubmitting(true);

    // Always read from ref (guaranteed fresh)
    const bags = { ...selectedBaggageRef.current };

    if (listing?.baggage_items?.length > 0) {
      onPurchase({
        selectedBaggage: bags,
        articleDescription,
        deliveryInstructions,
        paymentMethod,
        declaredValue: declaredValue > 0 ? declaredValue : 0,
        insuranceOptIn: insuranceOptIn && declaredValue > (insuranceInfo?.base_coverage || 100),
        isMultiBaggage: true
      });
    } else {
      onPurchase({
        quantity,
        articleDescription,
        deliveryInstructions,
        paymentMethod,
        declaredValue: declaredValue > 0 ? declaredValue : 0,
        insuranceOptIn: insuranceOptIn && declaredValue > (insuranceInfo?.base_coverage || 100),
        isMultiBaggage: false
      });
    }
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setSubmitting(false);
      setShowLegal(false);
      setCguAccepted(false);
      setRefundAccepted(false);
      setLogisticsAccepted(false);
      setDisclaimerAccepted(false);
    }
    onOpenChange(isOpen);
  };

  const isFormDisabled = () => {
    if (!isDescriptionValid) return true;
    if (listing?.baggage_items?.length > 0) return !hasSelection();
    return !quantity || quantity <= 0;
  };

  if (!listing) return null;

  const isMulti = listing.baggage_items && listing.baggage_items.length > 0;
  const totals = isMulti ? calculateTotal() : { subtotal, commission, insurance: insuranceCost, total };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button className="w-full btn-primary mb-3" size="lg" data-testid="buy-btn">
          <ShoppingCart className="w-5 h-5 mr-2" />Acheter maintenant
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {!disclaimerAccepted ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">Information importante</DialogTitle>
              <DialogDescription>Avant de réserver des kilos via KACOS</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 text-sm">
              <p className="text-muted-foreground">
                Assurez-vous que le contenu de votre colis respecte les règles de transport aérien et les réglementations douanières.
              </p>
              <div>
                <p className="font-medium text-red-500 mb-1.5">Articles interdits</p>
                <ul className="text-xs text-muted-foreground space-y-0.5 list-disc pl-4">
                  <li>Produits inflammables, explosifs ou chimiques dangereux</li>
                  <li>Armes, drogues ou substances illégales</li>
                  <li>Batteries au lithium non conformes</li>
                  <li>Liquides dangereux ou corrosifs</li>
                  <li>Argent liquide en grande quantité</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-amber-500 mb-1.5">Articles soumis à restrictions</p>
                <ul className="text-xs text-muted-foreground space-y-0.5 list-disc pl-4">
                  <li>Produits alimentaires, médicaments, cosmétiques liquides</li>
                  <li>Appareils électroniques avec batterie</li>
                  <li>Produits agricoles ou semences</li>
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1.5">Responsabilité de l'expéditeur</p>
                <p className="text-xs text-muted-foreground">
                  L'expéditeur est responsable du contenu du colis, du respect des règles des compagnies aériennes et des lois douanières du pays de destination. Les colis peuvent être refusés, inspectés ou confisqués par les autorités.
                </p>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Conseil KACOS :</span> Vérifiez que vos articles sont autorisés avant l'envoi. Kacos n'est ni responsable juridiquement, ni financièrement des frais de douane ou toute situation découlant du transport des produits.
                </p>
              </div>
              <Button className="w-full" onClick={() => setDisclaimerAccepted(true)} data-testid="accept-disclaimer-btn">
                Je suis d'accord
              </Button>
            </div>
          </>
        ) : (
          <>
        <DialogHeader>
          <DialogTitle>Confirmer l'achat</DialogTitle>
          <DialogDescription>{flag(listing.departure_country)} {listing.departure_city} → {flag(listing.destination_country)} {listing.destination_city}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">

          {/* === BAGGAGE SELECTION === */}
          {isMulti ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">Sélectionnez vos bagages *</label>
              {listing.baggage_items.map((item, index) => {
                const maxQty = item.available_quantity ?? item.quantity ?? 0;
                const qty = selectedBaggage[item.type] || 0;
                const options = [];
                for (let n = 0; n <= maxQty; n++) { options.push(n); }
                return (
                  <div key={index} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{BAGGAGE_TYPE_LABELS[item.type] || item.type}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.price_per_unit?.toFixed(2)} {listing.currency || 'CAD'}/unité • {maxQty} dispo
                        </p>
                      </div>
                      <select
                        value={qty}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          updateBaggage(prev => ({...prev, [item.type]: val}));
                        }}
                        className="h-10 w-20 rounded-md border border-input bg-background px-2 text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid={`select-qty-${item.type}`}
                      >
                        {options.map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    {qty > 0 && (
                      <p className="text-sm text-primary font-medium text-right mt-1">
                        = {(qty * item.price_per_unit).toFixed(2)} {listing.currency || 'CAD'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium">Quantité ({getBaggageUnit(listing.baggage_type, 1)})</label>
              <select
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="buy-quantity"
              >
                {Array.from({length: listing.available_quantity || 1}, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Max: {listing.available_quantity} disponibles</p>
            </div>
          )}

          {/* === DESCRIPTION === */}
          <div>
            <label className="text-sm font-medium">Description des articles *</label>
            <Textarea value={articleDescription}
              onChange={(e) => { setArticleDescription(e.target.value); setDescriptionTouched(true); }}
              placeholder="Décrivez les articles (min 10 caractères)..." rows={3} className="mt-1" data-testid="buy-description" />
            {showDescriptionError && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />Minimum {MIN_DESCRIPTION_LENGTH} caractères ({remainingChars} restants)
              </p>
            )}
          </div>

          {/* === DELIVERY INSTRUCTIONS === */}
          <div>
            <label className="text-sm font-medium">Consignes de livraison (optionnel)</label>
            <Textarea value={deliveryInstructions} onChange={(e) => setDeliveryInstructions(e.target.value)}
              placeholder="Nom du destinataire, téléphone, adresse..." rows={2} className="mt-1" data-testid="buy-delivery-instructions" />
          </div>

          {/* === INSURANCE === */}
          <div>
            <label className="text-sm font-medium flex items-center gap-1">
              <Shield className="w-4 h-4 text-primary" />Valeur déclarée (optionnel)
            </label>
            <Input type="number" min="0" value={declaredValue || ''} onChange={(e) => setDeclaredValue(parseFloat(e.target.value) || 0)}
              placeholder="Ex: 500" className="mt-1" data-testid="buy-declared-value" />
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <Info className="w-3 h-3" />Couverture gratuite jusqu'à {insuranceInfo?.base_coverage || 100} CAD
            </p>
          </div>

          {declaredValue > (insuranceInfo?.base_coverage || 100) && (
            <div className="bg-primary/10 p-3 rounded-lg space-y-2">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Assurance complémentaire</p>
                  <p className="text-xs text-muted-foreground">
                    Montant non couvert: {(declaredValue - (insuranceInfo?.base_coverage || 100)).toFixed(2)} CAD
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="insurance" checked={insuranceOptIn} onCheckedChange={setInsuranceOptIn} data-testid="buy-insurance-optin" />
                <label htmlFor="insurance" className="text-sm cursor-pointer">
                  Souscrire (+{((declaredValue - (insuranceInfo?.base_coverage || 100)) * (insuranceInfo?.rate || 0.02)).toFixed(2)} CAD)
                </label>
              </div>
            </div>
          )}

          {/* === PAYMENT METHOD === */}
          <div>
            <label className="text-sm font-medium">Mode de paiement</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {PAYMENT_METHODS.map((method) => (
                <div key={method.id}
                  className={`p-2.5 border rounded-lg cursor-pointer transition-all ${paymentMethod === method.id ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/30'}`}
                  onClick={() => setPaymentMethod(method.id)} data-testid={`payment-method-${method.id}`}
                >
                  <div className="flex items-center gap-2">
                    <method.icon className="w-4 h-4" />
                    <span className="text-xs font-medium">{method.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* === PRICE SUMMARY === */}
          <div className="bg-muted p-3 rounded-lg space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span>Sous-total</span>
              <span>{totals.subtotal.toFixed(2)} {listing.currency || 'CAD'}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Commission (10%)</span>
              <span>{totals.commission.toFixed(2)} {listing.currency || 'CAD'}</span>
            </div>
            {totals.insurance > 0 && (
              <div className="flex justify-between text-primary">
                <span>Assurance</span>
                <span>{totals.insurance.toFixed(2)} {listing.currency || 'CAD'}</span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t pt-1.5">
              <span>Total</span>
              <span className="text-primary">{totals.total.toFixed(2)} {listing.currency || 'CAD'}</span>
            </div>
          </div>

          {/* === LEGAL TERMS (inline, collapsible) === */}
          <div className="border rounded-lg">
            <button type="button" className="w-full p-3 flex items-center justify-between text-sm font-medium"
              onClick={() => setShowLegal(prev => !prev)} data-testid="toggle-legal-terms"
            >
              <span>Conditions légales</span>
              {showLegal ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showLegal && (
              <div className="px-3 pb-3 space-y-3 text-xs text-muted-foreground max-h-[200px] overflow-y-auto">
                <details>
                  <summary className="font-semibold text-foreground cursor-pointer text-sm">CGU</summary>
                  <p className="mt-1">La plateforme met en relation vendeurs et acheteurs via un système centralisé. L'administrateur agit comme intermédiaire logistique et financier. Les paiements sont traités via Stripe avec une commission de 10%. L'utilisateur doit fournir des informations exactes et être majeur. Les annonces sont soumises à approbation. Après livraison, l'acheteur dispose de 48h pour confirmer.</p>
                </details>
                <details>
                  <summary className="font-semibold text-foreground cursor-pointer text-sm">Remboursement</summary>
                  <p className="mt-1">Remboursement possible si : produit non conforme, endommagé avant livraison, contrefait, ou colis perdu. Demande dans les 48h suivant la livraison. L'administrateur analyse les preuves et la décision est finale.</p>
                </details>
                <details>
                  <summary className="font-semibold text-foreground cursor-pointer text-sm">Litiges</summary>
                  <p className="mt-1">L'acheteur peut ouvrir un litige dans les 48h. L'administrateur analyse les preuves. Issues possibles : validation, remboursement partiel/total, ou annulation.</p>
                </details>
                <details>
                  <summary className="font-semibold text-foreground cursor-pointer text-sm">Confidentialité</summary>
                  <p className="mt-1">Données collectées : nom, adresse, email, téléphone, informations de transaction. Données stockées de façon sécurisée, non vendues à des tiers. Droits d'accès, rectification et suppression conformément aux lois canadiennes.</p>
                </details>
                <details>
                  <summary className="font-semibold text-foreground cursor-pointer text-sm">Logistique</summary>
                  <p className="mt-1">Réception, inspection, stockage temporaire, transport et livraison. Responsabilité limitée aux dommages causés durant la possession. La plateforme peut refuser un colis non conforme, dangereux ou illégal.</p>
                </details>
              </div>
            )}
          </div>

          {/* === LEGAL CHECKBOXES === */}
          <div className="space-y-2.5">
            <div className="flex items-start space-x-3">
              <Checkbox id="cgu" checked={cguAccepted} onCheckedChange={setCguAccepted} data-testid="accept-cgu" />
              <label htmlFor="cgu" className="text-xs leading-tight cursor-pointer">
                J'accepte les <span className="text-primary font-medium">Conditions Générales d'Utilisation</span>
              </label>
            </div>
            <div className="flex items-start space-x-3">
              <Checkbox id="refund" checked={refundAccepted} onCheckedChange={setRefundAccepted} data-testid="accept-refund" />
              <label htmlFor="refund" className="text-xs leading-tight cursor-pointer">
                J'accepte la <span className="text-primary font-medium">Politique de Remboursement et Litiges</span>
              </label>
            </div>
            <div className="flex items-start space-x-3">
              <Checkbox id="logistics" checked={logisticsAccepted} onCheckedChange={setLogisticsAccepted} data-testid="accept-logistics" />
              <label htmlFor="logistics" className="text-xs leading-tight cursor-pointer">
                J'accepte la <span className="text-primary font-medium">Clause Logistique et Confidentialité</span>
              </label>
            </div>
          </div>

          {/* === SUBMIT === */}
          <Button className="w-full" onClick={handleSubmit}
            disabled={processing || submitting || isFormDisabled() || !allLegalAccepted}
            data-testid="confirm-legal-terms"
          >
            {processing || submitting
              ? 'Traitement en cours...'
              : `Confirmer et envoyer - ${totals.total.toFixed(2)} ${listing.currency || 'CAD'}`
            }
          </Button>
        </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseModal;
